import { Link } from "react-router-dom";

const LAST_UPDATED = "February 18, 2026";

export default function Privacy() {
  return (
    <main data-testid="page-privacy" className="pt-20 bg-[var(--bone)]">
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-20 lg:py-28 relative overflow-hidden">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <div className="eyebrow-light">Legal</div>
          <h1 className="font-display text-[44px] md:text-[64px] leading-[1.02] mt-5">Privacy Policy</h1>
          <p className="mt-6 text-[14px] tracking-[0.22em] uppercase text-[var(--navy-100)]/60">Last updated · {LAST_UPDATED}</p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-24 legal-prose">
        <p className="lead">
          Freedom In Me, LLC (“<strong>Freedom In Me</strong>,” “<strong>we</strong>,” “<strong>us</strong>,” or “<strong>our</strong>”), a North Carolina limited liability company, respects your privacy. This Privacy Policy explains what information we collect when you visit <a href="https://www.itsthefreedominme.com">itsthefreedominme.com</a>, <a href="https://align.itsthefreedominme.com">align.itsthefreedominme.com</a>, or any related Freedom In Me service (collectively, the “<strong>Site</strong>”), how we use that information, who we share it with, and the rights you have.
        </p>

        <h2>1. Who we are and how to contact us</h2>
        <p>
          Freedom In Me, LLC is a strategy and systems consultancy based in North Carolina, United States. For any privacy-related question or to exercise any right described below, contact us at <a href="mailto:info@itsthefreedominme.com">info@itsthefreedominme.com</a>.
        </p>

        <h2>2. Information we collect</h2>
        <h3>Information you provide directly</h3>
        <ul>
          <li><strong>Contact &amp; inquiry data</strong> — name, work email, company, role/title, phone (optional), employee count, inquiry scope, department, and the message you send via our Contact form.</li>
          <li><strong>Assessment data</strong> — your name, work email, company, role, and the answers you provide to the A.L.I.G.N.™ Readiness Index (twenty-five questions across five pillars).</li>
          <li><strong>Chat data</strong> — the messages you send to the Ask A.L.I.G.N. chat widget and the AI-generated responses, associated with an anonymous browser-scoped session ID.</li>
        </ul>

        <h3>Information collected automatically</h3>
        <ul>
          <li>Basic technical data including IP address, browser type, device type, referring URL, pages viewed, and timestamps.</li>
          <li>Essential cookies and equivalent local-storage values used to operate the Site (for example, remembering that you have dismissed the cookie banner).</li>
        </ul>

        <h2>3. How we use your information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Respond to inquiries you submit through the Contact form;</li>
          <li>Generate, deliver, and discuss your A.L.I.G.N.™ Readiness Index report;</li>
          <li>Operate the Ask A.L.I.G.N. conversational diagnostic;</li>
          <li>Send transactional emails related to your inquiry or assessment;</li>
          <li>Maintain, secure, and improve the Site;</li>
          <li>Comply with legal obligations and enforce our Terms of Use.</li>
        </ul>
        <p>We do <strong>not</strong> sell, rent, or trade your personal information.</p>

        <h2>4. Sub-processors who help us operate</h2>
        <p>We rely on a small number of trusted vendors (“sub-processors”) to operate the Site. Each is contractually required to protect your information and to use it only on our behalf:</p>
        <ul>
          <li><strong>Anthropic, PBC</strong> — provides the Claude AI model that powers the A.L.I.G.N. report and chat. <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noreferrer">Anthropic privacy policy</a>.</li>
          <li><strong>Resend, Inc.</strong> — sends transactional emails. <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noreferrer">Resend privacy policy</a>.</li>
          <li><strong>MongoDB, Inc. (Atlas)</strong> — hosts our database. <a href="https://www.mongodb.com/legal/privacy/privacy-policy" target="_blank" rel="noreferrer">MongoDB privacy policy</a>.</li>
          <li><strong>Vercel Inc.</strong> — hosts the front-end of the Site. <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noreferrer">Vercel privacy policy</a>.</li>
          <li><strong>Render Services, Inc.</strong> — hosts the back-end API. <a href="https://render.com/privacy" target="_blank" rel="noreferrer">Render privacy policy</a>.</li>
          <li><strong>Cloudflare, Inc.</strong> — provides edge networking and DDoS protection.</li>
        </ul>

        <h2>5. AI-generated content</h2>
        <p>
          Your A.L.I.G.N.™ Readiness Index report and Ask A.L.I.G.N. responses are generated by Claude, a large-language model operated by Anthropic. AI-generated content can contain inaccuracies and is provided for executive reflection — not as professional, legal, financial, medical, or fiduciary advice. Significant decisions should be confirmed with a member of the Freedom In Me team or your own advisors.
        </p>

        <h2>6. Legal bases (for visitors in the EU/UK)</h2>
        <p>Where the GDPR applies, we rely on the following legal bases: (a) <em>consent</em> — when you submit the Contact form, take the Assessment, or interact with the chat; (b) <em>legitimate interests</em> — to maintain, secure, and improve the Site; and (c) <em>legal obligation</em> — when required to retain or disclose information by law.</p>

        <h2>7. Data retention</h2>
        <p>We retain inquiry data, assessment data, and chat sessions for as long as we have a legitimate business reason to do so — typically up to <strong>seven (7) years</strong> for business records and tax purposes — unless you request earlier deletion.</p>

        <h2>8. Security</h2>
        <p>All traffic to the Site is encrypted in transit via TLS. Data is encrypted at rest by MongoDB Atlas. Administrative access is restricted to authorized Freedom In Me personnel and protected by token-based authentication. While no system is perfectly secure, we follow industry-standard practices and review them regularly. For more detail see our <Link to="/security">Security &amp; Trust</Link> page.</p>

        <h2>9. Your rights</h2>
        <p>Depending on where you live, you may have the right to:</p>
        <ul>
          <li>Access the personal information we hold about you;</li>
          <li>Request correction of inaccurate information;</li>
          <li>Request deletion of your information;</li>
          <li>Object to or restrict certain processing;</li>
          <li>Request a portable copy of your information;</li>
          <li>Withdraw consent at any time (without affecting prior lawful processing);</li>
          <li>Lodge a complaint with your local data-protection authority.</li>
        </ul>
        <p>To exercise any of these rights, email <a href="mailto:info@itsthefreedominme.com">info@itsthefreedominme.com</a>. We will respond within thirty (30) days.</p>

        <h2>10. California residents</h2>
        <p>If you are a California resident, the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA) give you the rights described above, including the right to know what information we have collected and the right to request deletion. We do not sell or share your personal information for cross-context behavioral advertising.</p>

        <h2>11. Children</h2>
        <p>The Site is intended for business audiences and is not directed to children under the age of thirteen (13). We do not knowingly collect information from children. If you believe we have, contact us and we will delete it.</p>

        <h2>12. International visitors</h2>
        <p>Freedom In Me operates from the United States. By using the Site, you understand that your information will be processed in the United States, which may have data-protection laws different from those in your jurisdiction.</p>

        <h2>13. Changes to this policy</h2>
        <p>We may update this Privacy Policy from time to time. When we do, we will update the “Last updated” date at the top of this page. Material changes will be highlighted on the Site.</p>

        <h2>14. Contact</h2>
        <p>
          Freedom In Me, LLC<br />
          North Carolina, United States<br />
          <a href="mailto:info@itsthefreedominme.com">info@itsthefreedominme.com</a>
        </p>
      </article>
    </main>
  );
}
